'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

// ── option sets ──────────────────────────────────────────────────────────────
const typeOptions = ['Short Films', 'Documentary', 'Features', 'Animation'];

const genreOptions = [
  'Drama', 'Horror', 'Comedy', 'Thriller', 'Sci-Fi', 'Fantasy',
  'Romance', 'Action', 'Crime', 'Mystery', 'Experimental',
  'International', 'Family', 'Historical', 'Biographical',
];

const awardsOptions = [
  { value: 'winner', label: 'Award Winners', description: 'Films that have won awards' },
  { value: 'nominated', label: 'Award Nominated', description: 'Films nominated for awards' },
  { value: 'official_selection', label: 'Official Selection', description: 'Festival official selections' },
];

// ── types ─────────────────────────────────────────────────────────────────────
type ModalType = 'type' | 'genre' | 'awards' | null;

interface FilterState {
  types: string[];
  genres: string[];
  awards: string[];
}

// ── Modal component ───────────────────────────────────────────────────────────
interface FilterModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

function FilterModal({ open, title, onClose, children, anchorRef }: FilterModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        modalRef.current && !modalRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div
        ref={modalRef}
        className="fixed z-50 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[min(92vw,420px)] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden fade-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/8 transition-all"
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>
        {/* Body */}
        <div className="px-5 py-4">{children}</div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function GenreChips() {
  const [activeTab, setActiveTab] = useState<'all' | 'type' | 'genre' | 'awards'>('all');
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const [filters, setFilters] = useState<FilterState>({ types: [], genres: [], awards: [] });

  const typeRef = useRef<HTMLButtonElement>(null);
  const genreRef = useRef<HTMLButtonElement>(null);
  const awardsRef = useRef<HTMLButtonElement>(null);

  const toggleType = (v: string) =>
    setFilters(f => ({ ...f, types: f.types.includes(v) ? f.types.filter(x => x !== v) : [...f.types, v] }));

  const toggleGenre = (v: string) =>
    setFilters(f => ({ ...f, genres: f.genres.includes(v) ? f.genres.filter(x => x !== v) : [...f.genres, v] }));

  const toggleAward = (v: string) =>
    setFilters(f => ({ ...f, awards: f.awards.includes(v) ? f.awards.filter(x => x !== v) : [...f.awards, v] }));

  const clearFilter = (key: keyof FilterState) =>
    setFilters(f => ({ ...f, [key]: [] }));

  const typeCount = filters.types.length;
  const genreCount = filters.genres.length;
  const awardsCount = filters.awards.length;

  const handleTabClick = (tab: 'all' | 'type' | 'genre' | 'awards') => {
    if (tab === 'all') {
      setActiveTab('all');
      setOpenModal(null);
      setFilters({ types: [], genres: [], awards: [] });
    } else {
      setActiveTab(tab);
      setOpenModal(openModal === tab ? null : tab as ModalType);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 2xl:px-16 py-4 sm:py-6">
      <div className="flex items-center gap-2">
        {/* ALL */}
        <button
          onClick={() => handleTabClick('all')}
          className={`genre-chip shrink-0 ${activeTab === 'all' && typeCount === 0 && genreCount === 0 && awardsCount === 0 ? 'active' : ''}`}
        >
          All
        </button>

        {/* TYPE */}
        <button
          ref={typeRef}
          onClick={() => handleTabClick('type')}
          className={`genre-chip shrink-0 flex items-center gap-1.5 ${typeCount > 0 || (activeTab === 'type') ? 'active' : ''}`}
        >
          Type
          {typeCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
              {typeCount}
            </span>
          )}
          <Icon name={openModal === 'type' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={11} />
        </button>

        {/* GENRE */}
        <button
          ref={genreRef}
          onClick={() => handleTabClick('genre')}
          className={`genre-chip shrink-0 flex items-center gap-1.5 ${genreCount > 0 || (activeTab === 'genre') ? 'active' : ''}`}
        >
          Genre
          {genreCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
              {genreCount}
            </span>
          )}
          <Icon name={openModal === 'genre' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={11} />
        </button>

        {/* AWARDS */}
        <button
          ref={awardsRef}
          onClick={() => handleTabClick('awards')}
          className={`genre-chip shrink-0 flex items-center gap-1.5 ${awardsCount > 0 || (activeTab === 'awards') ? 'active' : ''}`}
        >
          Awards
          {awardsCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
              {awardsCount}
            </span>
          )}
          <Icon name={openModal === 'awards' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={11} />
        </button>

        {/* Active filter summary chips */}
        {(typeCount > 0 || genreCount > 0 || awardsCount > 0) && (
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide ml-1">
            {filters.types.map(t => (
              <span key={`at-${t}`} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[11px] font-medium shrink-0">
                {t}
                <button onClick={() => toggleType(t)} className="hover:text-white transition-colors ml-0.5">
                  <Icon name="XMarkIcon" size={10} />
                </button>
              </span>
            ))}
            {filters.genres.map(g => (
              <span key={`ag-${g}`} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[11px] font-medium shrink-0">
                {g}
                <button onClick={() => toggleGenre(g)} className="hover:text-white transition-colors ml-0.5">
                  <Icon name="XMarkIcon" size={10} />
                </button>
              </span>
            ))}
            {filters.awards.map(a => (
              <span key={`aa-${a}`} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[11px] font-medium shrink-0">
                🏆 {awardsOptions.find(o => o.value === a)?.label ?? a}
                <button onClick={() => toggleAward(a)} className="hover:text-white transition-colors ml-0.5">
                  <Icon name="XMarkIcon" size={10} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── TYPE MODAL ── */}
      <FilterModal
        open={openModal === 'type'}
        title="Filter by Type"
        onClose={() => setOpenModal(null)}
        anchorRef={typeRef}
      >
        <div className="flex flex-col gap-2">
          {typeOptions.map(t => {
            const selected = filters.types.includes(t);
            return (
              <button
                key={`tm-${t}`}
                onClick={() => toggleType(t)}
                className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                  selected
                    ? 'border-primary/50 bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground hover:bg-white/4'
                }`}
              >
                <span>{t}</span>
                {selected && <Icon name="CheckIcon" size={16} className="text-primary" />}
              </button>
            );
          })}
        </div>
        {typeCount > 0 && (
          <button
            onClick={() => { clearFilter('types'); }}
            className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
          >
            Clear selection
          </button>
        )}
      </FilterModal>

      {/* ── GENRE MODAL ── */}
      <FilterModal
        open={openModal === 'genre'}
        title="Filter by Genre"
        onClose={() => setOpenModal(null)}
        anchorRef={genreRef}
      >
        <div className="flex flex-wrap gap-2">
          {genreOptions.map(g => {
            const selected = filters.genres.includes(g);
            return (
              <button
                key={`gm-${g}`}
                onClick={() => toggleGenre(g)}
                className={`genre-chip text-xs ${selected ? 'active' : ''}`}
              >
                {g}
              </button>
            );
          })}
        </div>
        {genreCount > 0 && (
          <button
            onClick={() => clearFilter('genres')}
            className="mt-4 w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
          >
            Clear selection
          </button>
        )}
      </FilterModal>

      {/* ── AWARDS MODAL ── */}
      <FilterModal
        open={openModal === 'awards'}
        title="Filter by Awards"
        onClose={() => setOpenModal(null)}
        anchorRef={awardsRef}
      >
        <p className="text-xs text-muted-foreground mb-3">Show films based on their awards history</p>
        <div className="flex flex-col gap-2">
          {awardsOptions.map(opt => {
            const selected = filters.awards.includes(opt.value);
            return (
              <button
                key={`am-${opt.value}`}
                onClick={() => toggleAward(opt.value)}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border text-left transition-all ${
                  selected
                    ? 'border-amber-500/50 bg-amber-500/10 text-amber-300' :'border-border text-muted-foreground hover:border-amber-500/30 hover:text-foreground hover:bg-white/4'
                }`}
              >
                <span className="text-lg leading-none">🏆</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${selected ? 'text-amber-300' : 'text-foreground'}`}>{opt.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                </div>
                {selected && <Icon name="CheckIcon" size={16} className="text-amber-400 shrink-0" />}
              </button>
            );
          })}
        </div>
        {awardsCount > 0 && (
          <button
            onClick={() => clearFilter('awards')}
            className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
          >
            Clear selection
          </button>
        )}
      </FilterModal>
    </div>
  );
}